import {
  Component,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { Home, RefreshCw, TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return {
      hasError: true,
    };
  }

  componentDidCatch(
    error: Error,
    errorInfo: ErrorInfo,
  ) {
    console.error(
      "Unhandled React rendering error:",
      error,
      errorInfo,
    );
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-12">
        <Card className="w-full max-w-lg border-slate-800 bg-slate-900 text-white shadow-2xl">
          <CardContent className="flex flex-col items-center px-8 py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10 text-red-400">
              <TriangleAlert className="h-8 w-8" />
            </div>

            <p className="mt-6 text-sm font-semibold uppercase tracking-[0.3em] text-red-400">
              Error 500
            </p>

            <h1 className="mt-3 text-3xl font-semibold tracking-tight">
              Something went wrong
            </h1>

            <p className="mt-4 max-w-md text-sm leading-6 text-slate-400">
              MinistryIQ encountered an unexpected error.
              Reload the page to try again. If the problem
              continues, return to the dashboard.
            </p>

            <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                type="button"
                onClick={this.handleReload}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload page
              </Button>

              <Button
                type="button"
                variant="secondary"
                onClick={this.handleGoHome}
              >
                <Home className="mr-2 h-4 w-4" />
                Go to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}
